import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Zap, Building2, Sparkles } from "lucide-react";

/**
 * V232 — Live counters strip for landing pages (upgraded).
 *
 * Changes vs V231.7:
 *   - Each counter card now shows a 7-day inline sparkline (SVG, zero deps)
 *     pulled from /stats/public-counters/trend. If the trend endpoint 404s
 *     or returns empty data, the sparkline is simply hidden.
 *   - Added a tiny delta chip (▲ +N za 7 dní) to anchor the perceived
 *     growth rate — classic "numbers go up and to the right" dopamine.
 *
 * Why not fake? Google crawls these; a static number would hurt E-E-A-T.
 * The counters tick up as actual firms get scanned, so content IS unique
 * per crawl visit — a key difference from a hard-coded "1 234 happy
 * customers" fake. Same rule applies to the sparkline: it's derived from
 * `first_viewed_at` bucketing of real cache rows.
 */
export default function LiveCounters({ variant = "dark" }) {
  const [counts, setCounts] = useState(null);
  const [trend, setTrend] = useState(null);
  const [animated, setAnimated] = useState({ audits: 0, firms: 0, citations: 0 });
  const animRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/stats/public-counters`).then((r) => {
      if (cancelled) return;
      setCounts(r.data);
      // Animate from 0 to real values over 1.2s with ease-out.
      const start = performance.now();
      const dur = 1200;
      const easeOut = (t) => 1 - Math.pow(1 - t, 3);
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const e = easeOut(t);
        setAnimated({
          audits: Math.round(r.data.audits_count * e),
          firms: Math.round(r.data.firms_in_db * e),
          citations: Math.round(r.data.citations_est * e),
        });
        if (t < 1) animRef.current = requestAnimationFrame(step);
      };
      animRef.current = requestAnimationFrame(step);
    }).catch(() => {});

    axios.get(`${API}/stats/public-counters/trend`).then((r) => {
      if (!cancelled) setTrend(r.data);
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const fmt = (n) => (n || 0).toLocaleString("sk-SK");
  const isDark = variant === "dark";

  const cardCls = isDark
    ? "bg-white/5 border border-white/10 backdrop-blur-sm"
    : "bg-slate-50 border border-slate-200";
  const labelCls = isDark ? "text-slate-400" : "text-slate-500";
  const valueCls = isDark ? "text-white" : "text-slate-900";
  const iconCls = isDark ? "text-amber-400" : "text-amber-600";
  const sparkCls = isDark ? "text-amber-400" : "text-amber-600";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="live-counters">
      <Counter
        icon={Zap}
        label="AI SEO auditov zmerané"
        value={fmt(animated.audits)}
        hint={counts ? "tiká v reálnom čase" : "načítavam…"}
        series={trend?.audits}
        sparkCls={sparkCls}
        cardCls={cardCls} labelCls={labelCls} valueCls={valueCls} iconCls={iconCls}
        testid="counter-audits"
      />
      <Counter
        icon={Building2}
        label="SK + CZ firiem v databáze"
        value={fmt(animated.firms)}
        hint="ORSF.sk · ARES.gov.cz"
        series={trend?.firms}
        sparkCls={sparkCls}
        cardCls={cardCls} labelCls={labelCls} valueCls={valueCls} iconCls={iconCls}
        testid="counter-firms"
      />
      <Counter
        icon={Sparkles}
        label="ChatGPT · Perplexity · Gemini citácií odhadovaných"
        value={fmt(animated.citations)}
        hint="agregované z firm scanov"
        series={trend?.citations}
        sparkCls={sparkCls}
        cardCls={cardCls} labelCls={labelCls} valueCls={valueCls} iconCls={iconCls}
        testid="counter-citations"
      />
    </div>
  );
}

function Counter({ icon: Icon, label, value, hint, series, sparkCls, cardCls, labelCls, valueCls, iconCls, testid }) {
  const delta = Array.isArray(series) ? series.reduce((a, b) => a + b, 0) : 0;
  return (
    <div className={`rounded-xl p-5 ${cardCls}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <Icon className={`w-5 h-5 ${iconCls}`} />
        {delta > 0 && (
          <span
            className={`text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20`}
            data-testid={`${testid}-delta`}
          >
            ▲ +{delta.toLocaleString("sk-SK")} / 7d
          </span>
        )}
      </div>
      <div className={`text-3xl sm:text-4xl font-bold tracking-tight tabular-nums ${valueCls}`}>
        {value}
      </div>
      <div className={`text-xs mt-2 ${labelCls} leading-snug`}>{label}</div>
      <div className="flex items-end justify-between gap-3 mt-2">
        <div className={`text-[10px] ${labelCls} opacity-70`}>{hint}</div>
        <Sparkline series={series} className={sparkCls} />
      </div>
    </div>
  );
}

/**
 * Inline SVG sparkline — no dependencies, respects currentColor.
 * Renders an area + line. Hidden when there's no data or every value is 0.
 */
function Sparkline({ series, className = "" }) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const max = Math.max(...series, 1);
  const w = 72;
  const h = 20;
  const stepX = series.length > 1 ? w / (series.length - 1) : w;
  const pts = series.map((v, i) => [i * stepX, h - (v / max) * (h - 2) - 1]);
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const areaD = `${pathD} L ${w} ${h} L 0 ${h} Z`;
  if (series.every((v) => v === 0)) return null;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      className={`${className}`}
      data-testid="counter-sparkline"
      aria-hidden="true"
    >
      <path d={areaD} fill="currentColor" opacity="0.15" />
      <path d={pathD} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.length > 0 && (
        <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="1.8" fill="currentColor" />
      )}
    </svg>
  );
}
